import React, { useState, useMemo, useCallback, memo } from 'react';
import { Box } from '@mui/material';
import LoadableMonacoEditor from 'js/components/elements/editors/monaco/LoadableMonacoEditor';
import { getEditorLanguage } from 'js/components/elements/editors/monaco/MonacoEditorUtils';
import {
  PreviewResult,
  ExcelPreviewData,
  ExcelSheetData,
  WordPreviewData,
  isExcelPreviewData,
  isWordPreviewData,
} from '../utils/previewUtils';
import styles from './FilePreviewContent.module.scss';

// ============================================
// Constants
// ============================================
const MONACO_EDITOR_OPTIONS = {
  readOnly: true,
  minimap: { enabled: false },
  wordWrap: 'on' as const,
  lineNumbers: 'on' as const,
  scrollBeyondLastLine: false,
  fontSize: 13,
  fontFamily: "'JetBrains Mono', 'Fira Code', Monaco, Consolas, monospace",
};

const HEADER_HEIGHT_OFFSET = 250;

// ============================================
// Types
// ============================================
interface FilePreviewContentProps {
  /** The preview result data */
  preview: PreviewResult;
  /** Whether the preview is currently loading */
  isLoading: boolean;
  /** Name of the file being previewed */
  fileName: string;
}

interface StatusDisplayProps {
  icon: string;
  title: string;
  message: string;
  variant?: 'error' | 'unsupported' | 'info';
}

type WordViewMode = 'formatted' | 'text';

// ============================================
// Utility Functions
// ============================================

/**
 * Converts a zero-based column index to Excel-style column letter.
 * @example getColumnLetter(0) returns 'A'
 * @example getColumnLetter(25) returns 'Z'
 * @example getColumnLetter(26) returns 'AA'
 */
function getColumnLetter(index: number): string {
  let letter = '';
  let temp = index;
  while (temp >= 0) {
    letter = String.fromCharCode((temp % 26) + 65) + letter;
    temp = Math.floor(temp / 26) - 1;
  }
  return letter;
}

/**
 * Formats a cell value for display, handling null/undefined values.
 */
function formatCellValue(value: string | number | boolean | null): string {
  if (value === null || value === undefined) {
    return '';
  }
  return String(value);
}

// ============================================
// Reusable Components
// ============================================

/**
 * Displays a status message with icon (loading, error, unsupported, etc.)
 */
const StatusDisplay = memo(function StatusDisplay({
  icon,
  title,
  message,
  variant = 'info',
}: StatusDisplayProps) {
  const iconClassName = variant === 'error' ? styles.errorIcon : styles.unsupportedIcon;

  const containerClassName =
    variant === 'error' ? styles.errorContainer : styles.unsupportedContainer;

  return (
    <div className={containerClassName}>
      <div className={`${styles.statusIcon} ${iconClassName}`}>{icon}</div>
      <h3 className={styles.statusTitle}>{title}</h3>
      <p className={styles.statusMessage}>{message}</p>
    </div>
  );
});

/**
 * Loading spinner component
 */
const LoadingSpinner = memo(function LoadingSpinner() {
  return (
    <div className={styles.loadingContainer}>
      <div className={styles.loadingSpinner} />
      <span className={styles.statusMessage}>Loading preview...</span>
    </div>
  );
});

// ============================================
// Word Document Preview
// ============================================

interface WordDocumentProps {
  data: WordPreviewData;
}

/**
 * Renders a Word document with formatted HTML or plain text view.
 */
const WordDocument = memo(function WordDocument({ data }: WordDocumentProps) {
  const [viewMode, setViewMode] = useState<WordViewMode>('formatted');

  const handleViewModeChange = useCallback((mode: WordViewMode) => {
    setViewMode(mode);
  }, []);

  const hasWarnings = data.messages.length > 0;

  return (
    <div className={styles.wordContainer}>
      <div className={styles.wordToolbar}>
        <div className={styles.viewToggle}>
          <button
            type="button"
            className={`${styles.viewToggleBtn} ${viewMode === 'formatted' ? styles.viewToggleBtnActive : ''}`}
            onClick={() => handleViewModeChange('formatted')}
            aria-pressed={viewMode === 'formatted'}
          >
            📄 Formatted
          </button>
          <button
            type="button"
            className={`${styles.viewToggleBtn} ${viewMode === 'text' ? styles.viewToggleBtnActive : ''}`}
            onClick={() => handleViewModeChange('text')}
            aria-pressed={viewMode === 'text'}
          >
            📝 Plain Text
          </button>
        </div>
        {hasWarnings && (
          <div className={styles.wordWarnings} role="alert">
            ⚠️ {data.messages.length} conversion note(s)
          </div>
        )}
      </div>

      <div className={styles.wordDocumentWrapper}>
        <div className={styles.wordPage}>
          {viewMode === 'formatted' ? (
            <div
              className={styles.wordContent}
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{ __html: data.html }}
            />
          ) : (
            <pre className={styles.wordTextContent}>{data.text}</pre>
          )}
        </div>
      </div>
    </div>
  );
});

// ============================================
// Excel Spreadsheet Preview
// ============================================

interface ExcelSpreadsheetProps {
  data: ExcelPreviewData;
}

interface SheetTabsProps {
  sheets: ExcelSheetData[];
  activeIndex: number;
  onTabChange: (index: number) => void;
  isLimited: boolean;
  activeSheet: ExcelSheetData;
  columnCount: number;
}

/**
 * Sheet tabs component for Excel preview
 */
const SheetTabs = memo(function SheetTabs({
  sheets,
  activeIndex,
  onTabChange,
  isLimited,
  activeSheet,
  columnCount,
}: SheetTabsProps) {
  if (sheets.length <= 1) {
    return null;
  }

  return (
    <div className={styles.sheetTabsContainer}>
      <div className={styles.sheetTabs} role="tablist">
        {sheets.map((sheet, index) => (
          <button
            key={sheet.name}
            type="button"
            role="tab"
            aria-selected={index === activeIndex}
            className={`${styles.sheetTab} ${index === activeIndex ? styles.sheetTabActive : ''}`}
            onClick={() => onTabChange(index)}
          >
            {sheet.name}
          </button>
        ))}
      </div>
      {isLimited && (
        <div className={styles.sheetInfo}>
          Showing {activeSheet.data.length} of {activeSheet.totalRows} rows
          {columnCount < activeSheet.totalCols &&
            `, ${columnCount} of ${activeSheet.totalCols} columns`}
        </div>
      )}
    </div>
  );
});

/**
 * Renders an Excel spreadsheet with sheet tabs and table view.
 */
const ExcelSpreadsheet = memo(function ExcelSpreadsheet({ data }: ExcelSpreadsheetProps) {
  const [activeSheetIndex, setActiveSheetIndex] = useState(data.activeSheet);

  const activeSheet = data.sheets[activeSheetIndex];

  const handleTabChange = useCallback((index: number) => {
    setActiveSheetIndex(index);
  }, []);

  // Memoize column headers to avoid recalculation on each render
  const columnHeaders = useMemo(() => {
    if (!activeSheet || activeSheet.data.length === 0) {
      return [];
    }
    const count = activeSheet.data[0]?.length || 0;
    return Array.from({ length: count }, (_, i) => getColumnLetter(i));
  }, [activeSheet]);

  if (!activeSheet || activeSheet.data.length === 0) {
    return (
      <StatusDisplay
        icon="📊"
        title="Empty Sheet"
        message="This sheet appears to be empty."
        variant="unsupported"
      />
    );
  }

  const columnCount = columnHeaders.length;
  const isLimited =
    activeSheet.data.length < activeSheet.totalRows || columnCount < activeSheet.totalCols;

  return (
    <div className={styles.excelContainer}>
      <SheetTabs
        sheets={data.sheets}
        activeIndex={activeSheetIndex}
        onTabChange={handleTabChange}
        isLimited={isLimited}
        activeSheet={activeSheet}
        columnCount={columnCount}
      />

      <div className={styles.spreadsheetWrapper}>
        <table className={styles.spreadsheet}>
          <thead>
            <tr>
              <th className={styles.rowHeader} aria-label="Row number" />
              {columnHeaders.map((letter, i) => (
                <th key={i} className={styles.columnHeader}>
                  {letter}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {activeSheet.data.map((row, rowIndex) => (
              <tr key={rowIndex} className={rowIndex === 0 ? styles.headerRow : undefined}>
                <td className={styles.rowHeader}>{rowIndex + 1}</td>
                {row.map((cell, colIndex) => (
                  <td
                    key={colIndex}
                    className={`${styles.cell} ${rowIndex === 0 ? styles.headerCell : ''}`}
                    title={formatCellValue(cell)}
                  >
                    <span className={styles.cellContent}>{formatCellValue(cell)}</span>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
});

// ============================================
// Main Component
// ============================================

/**
 * Renders file preview content based on the file type.
 * Supports images, PDFs, Excel spreadsheets, Word documents, and text files.
 */
export function FilePreviewContent({ preview, isLoading, fileName }: FilePreviewContentProps) {
  const editorHeight = window.innerHeight - HEADER_HEIGHT_OFFSET;

  // Loading state
  if (isLoading) {
    return <LoadingSpinner />;
  }

  // Error state
  if (preview.error) {
    return (
      <StatusDisplay
        icon="⚠️"
        title="Preview Unavailable"
        message={String(preview.data)}
        variant="error"
      />
    );
  }

  // Render based on preview type
  switch (preview.type) {
    case 'image':
      return (
        <Box className={styles.imagePreviewContainer}>
          <img
            src={preview.objectUrl || String(preview.data)}
            alt={fileName}
            className={styles.imagePreview}
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = 'none';
            }}
          />
        </Box>
      );

    case 'pdf':
      if (preview.objectUrl) {
        return (
          <Box className={styles.pdfPreviewContainer}>
            <iframe
              src={preview.objectUrl}
              title={`PDF Preview: ${fileName}`}
              className={styles.pdfPreview}
              style={{ width: '100%', height: `${editorHeight}px`, border: 'none' }}
            />
          </Box>
        );
      }
      return (
        <Box className={styles.pdfPreviewContainer}>
          <StatusDisplay
            icon="📄"
            title="PDF Preview"
            message="PDF preview is not available. Please download the file to view it."
            variant="unsupported"
          />
        </Box>
      );

    case 'excel':
      if (isExcelPreviewData(preview.data)) {
        return <ExcelSpreadsheet data={preview.data} />;
      }
      return (
        <StatusDisplay
          icon="📊"
          title="Excel Preview Error"
          message={String(preview.data)}
          variant="error"
        />
      );

    case 'word':
      if (isWordPreviewData(preview.data)) {
        return <WordDocument data={preview.data} />;
      }
      return (
        <StatusDisplay
          icon="📄"
          title="Word Preview Error"
          message={String(preview.data)}
          variant="error"
        />
      );

    case 'text':
      return (
        <div className={styles.textPreviewContainer}>
          <LoadableMonacoEditor
            width="100%"
            height={editorHeight}
            value={String(preview.data || '')}
            language={getEditorLanguage('text')}
            options={MONACO_EDITOR_OPTIONS}
          />
        </div>
      );

    case 'unsupported':
    default:
      return (
        <StatusDisplay
          icon="📁"
          title="Preview Not Available"
          message={String(preview.data)}
          variant="unsupported"
        />
      );
  }
}
